import _ from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'

import StaticDatePickerInput from '@/components/ui/inputs/StaticDatePickerInput'
import TimePickerInput from '@/components/ui/inputs/TimePickerInput'
import { InputLabel } from '@mui/material'

dayjs.extend(duration)

type SelectDateAndTimePickerProps = {
  children: React.ReactNode
  currentDateTime?: Dayjs
  datePickerValue?: Dayjs | null
  error?: boolean
  onTimePickerValueChange?: (value: Dayjs | null) => void
  timePickerValue?: Dayjs | null
}

export type SelectStartDateStepProps = {
  currentDateTime: Dayjs
  defaultStartDate?: Date
  onSelect?: (startDate: string | null, error?: string | null) => void
}

const SelectDateAndTimePicker: React.FunctionComponent<SelectDateAndTimePickerProps> = ({
  children,
  currentDateTime,
  datePickerValue,
  error,
  onTimePickerValueChange,
  timePickerValue,
}) => {
  const { t } = useTranslation()

  const timePickerOptions = useMemo(() => {
    let timeInterval = 15 // minutes
    let closingTime = '20:00'
    return _.filter(_.range(24 * (60 / timeInterval)), (time) =>
      dayjs(datePickerValue)
        .startOf('day')
        .add(time * timeInterval, 'minutes')
        .isBetween(
          currentDateTime,
          dayjs(datePickerValue)
            .startOf('day')
            .add(dayjs.duration({ hours: parseInt(closingTime.split(':')[0]), minutes: parseInt(closingTime.split(':')[1]) }).asMilliseconds(), 'milliseconds'),
          undefined,
          '(]'
        )
    ).map((time) =>
      dayjs()
        .startOf('day')
        .add(timeInterval * time, 'minutes')
        .format('HH:mm')
    )
  }, [currentDateTime, datePickerValue])

  return (
    <Grid container>
      <Grid item xs={12} md={8}>
        <InputLabel sx={{ width: '320px', margin: '0 auto', pl: '24px', color: '#374151', fontSize: '0.875rem', fontWeight: 500 }}>
          {t('page.createConsultation.step.startDateSelection.startDateField.label')}
        </InputLabel>
        {children}
      </Grid>
      <Grid item xs={12} md={4}>
        <TimePickerInput
          error={error}
          disabled={!datePickerValue}
          label={t('page.createConsultation.step.startDateSelection.startTimeField.label')}
          value={timePickerValue ?? null}
          onChange={onTimePickerValueChange!}
          options={timePickerOptions}
        />
      </Grid>
    </Grid>
  )
}

export function SelectStartDateStep(props: SelectStartDateStepProps) {
  const { currentDateTime, defaultStartDate, onSelect } = props
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate ?? null)
  const [datePickerValue, setDatePickerValue] = useState<Dayjs | null>(defaultStartDate ? dayjs(defaultStartDate) : null)
  const [timePickerValue, setTimePickerValue] = useState<Dayjs | null>(defaultStartDate ? dayjs(defaultStartDate) : null)

  const handleDatePickerValueChange = (date: Dayjs | null) => {
    setDatePickerValue(date)
    const newStartDate =
      date && timePickerValue && dayjs(timePickerValue).isValid()
        ? dayjs(date).set('hour', timePickerValue.get('hour')).set('minute', timePickerValue.get('minute')).toDate()
        : null
    setStartDate(newStartDate)
    onSelect?.(newStartDate ? newStartDate.toISOString() : null)
  }

  const handleTimePickerValueChange = (time: Dayjs | null) => {
    setTimePickerValue(time)
    const newStartDate =
      datePickerValue && time && dayjs(time).isValid() ? dayjs(datePickerValue).set('hour', time.get('hour')).set('minute', time.get('minute')).toDate() : null
    setStartDate(newStartDate)
    onSelect?.(newStartDate ? newStartDate.toISOString() : null)
  }

  const handleError = (reason: string | null) => {
    onSelect?.(startDate ? startDate.toISOString() : null, reason)
  }

  let error = startDate ? dayjs(startDate).isBefore(currentDateTime, 'minutes') : false

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: '16px 10px' }}>
        <Stack spacing={10} direction="row" alignItems="center" justifyContent="center" sx={{ '& .MuiPickerStaticWrapper-root': { width: '100%' } }}>
          <StaticDatePickerInput
            components={{
              ActionBar: () => null,
              PaperContent: SelectDateAndTimePicker,
            }}
            componentsProps={{
              paperContent: { currentDateTime, datePickerValue, error, onTimePickerValueChange: handleTimePickerValueChange, timePickerValue },
            }}
            disablePast
            onChange={handleDatePickerValueChange}
            onError={handleError}
            ToolbarComponent={() => null}
            value={datePickerValue}
          />
        </Stack>
      </Paper>
    </Container>
  )
}

export default SelectStartDateStep
